import { Card, CardContent } from "@mui/material";
import { useTranslation } from "react-i18next";
import { tpTheme } from "@trainingpeaks/designtokens";
import { Subscription } from "@appTypes/index";
import { SUBSCRIPTION_SOURCES, SUBSCRIPTION_PRODUCT_TYPES } from "@constants/subscriptions";
import StatusChip from "@components/Common/StatusChip";
import SubscriptionInfo from "@components/Subscriptions/SubscriptionInfo";
import SubscriptionRenewalInfo from "@components/Subscriptions/SubscriptionRenewalInfo";
import SubscriptionManageMenu from "@components/Subscriptions/SubscriptionManageMenu";
import useHandleWinbackFlow from "@hooks/useHandleWinbackFlow";
import useHandleCoachMatchCancelFlow from "@hooks/useHandleCoachMatchCancelFlow";
import { useSubscriptionContext } from "@context/SubscriptionContext";
import { useNotificationContext } from "@context/NotificationContext";
import {
  deriveSubscriptionStatus,
  getSubscriptionStatusColors,
  getManageMenuItems,
} from "@utils/getSubscriptionDisplayInformation";
import useSegment from "@hooks/useSegment";
import { ANALYTICS_EVENTS, ANALYTICS_LOCATIONS } from "@appTypes/analytics";

interface SubscriptionCardProps {
  subscription: Subscription;
}

const SubscriptionCard = ({ subscription }: SubscriptionCardProps) => {
  const { t } = useTranslation();
  const { trackEvent } = useSegment();
  const { handleWinbackModalOpen, modal } = useHandleWinbackFlow(subscription);
  const {
    handleCoachMatchCancelModalOpen,
    modal: coachMatchCancelModal,
  } = useHandleCoachMatchCancelFlow(subscription);
  const { updateSubscriptionLocally } = useSubscriptionContext();
  const { showError, showSuccess } = useNotificationContext();
  const statusColors = getSubscriptionStatusColors(subscription);
  const status = deriveSubscriptionStatus(subscription);
  const handleSegmentTracking = (feature: string, details?: string) => {
    trackEvent(ANALYTICS_EVENTS.FEATURE_ENGAGED, feature, ANALYTICS_LOCATIONS.SUBSCRIPTIONS_LIST, details);
  };
  const menuItems = getManageMenuItems(
    subscription,
    updateSubscriptionLocally,
    handleWinbackModalOpen,
    showError,
    showSuccess,
    handleSegmentTracking,
    handleCoachMatchCancelModalOpen
  );
  //TODO: make only most recent SelfPaidPremium sub display
  const isMobileSub = subscription.source === SUBSCRIPTION_SOURCES.GOOGLE
    || subscription.source === SUBSCRIPTION_SOURCES.APPLE;
  const manageAndRenewalDivStyleClass = (() => {
    if(!menuItems.length && !isMobileSub) {
      return "justify-end";
    }

    if(isMobileSub) {
      return "justify-start";
    }

    return "justify-between";
  })();
  const cardPaddingClass = isMobileSub ? "pt-4 px-4 pb-0" : "p-4";
  const cardBaseClass = `mb-4 mx-auto ${cardPaddingClass} rounded-lg min-h-[8rem] w-full md:w-4/5 lg:w-3/5`;
  const cardBackgroundClass = subscription.subscriptionType === SUBSCRIPTION_PRODUCT_TYPES.SELF_PAID_PREMIUM
    ? "bg-coolGray-50"
    : "";
  const cardContentJustifyClass = isMobileSub ? "sm:justify-around" : "sm:justify-between";
  const bottomDivMarginClass = isMobileSub ? "" : "mt-6 ";
  const bottomDivClass = `min-h-[2rem] border-t flex items-end ${bottomDivMarginClass}${manageAndRenewalDivStyleClass}`;

  return (
    <>
      <Card
        className={`${cardBaseClass}${" "}${cardBackgroundClass} shadow-card`}
        sx={{ bgcolor: "coolGray.300" }}
        data-testid="subscription-card"
      >
        <CardContent
          className={`last:pb-0 last:pt-0 h-full flex flex-col justify-around ${cardContentJustifyClass}`}
          data-testid="subscription-card-content"
        >
          <div className="flex items-start justify-between">
            <SubscriptionInfo subscription={subscription} className={isMobileSub ? "pb-2" : undefined} />
            <StatusChip label={t(status)} {...statusColors} />
          </div>
          <div
            className={bottomDivClass}
            style={{ borderColor: tpTheme.semanticLayer.colors.divider.light }}
          >
            <SubscriptionManageMenu menuItems={menuItems} />
            <SubscriptionRenewalInfo subscription={subscription} status={status} />
          </div>
        </CardContent>
      </Card>
      {modal}
      {coachMatchCancelModal}
    </>
  );
};

export default SubscriptionCard;